import { apiClient } from '../../../Utilities/apiClient';

export interface Country {
  id: number;
  name: string;
  bbox: [number, number, number, number];
}

export interface City {
  id: number;
  name: string;
  countryId: number;
}

export interface CityWithCountry extends City {
  country: Country;
}

export interface CreateCountryRequest {
  name: string;
  bbox: [number, number, number, number];
}

export interface UpdateCountryRequest {
  id: number;
  name: string;
  bbox?: [number, number, number, number];
}

export interface CreateCityRequest {
  name: string;
  countryId: number;
}

export interface UpdateCityRequest {
  id: number;
  name: string;
  countryId: number;
}

export interface DeleteLocationRequest {
  id: number;
}

export interface DeletionPermissionCheck {
  canDelete: boolean;
  reason?: string;
  associatedCount?: number;
  associationType?: string;
}

export const locationService = {
  // Country operations
  getCountries: async (): Promise<{ data: { countries: Country[]; total: number } }> => {
    return apiClient.get('/trails/locations/countries');
  },

  createCountry: async (country: CreateCountryRequest): Promise<{ data: { country: Country } }> => {
    return apiClient.post('/trails/locations/countries', country);
  },

  updateCountry: async (country: UpdateCountryRequest): Promise<{ data: { country: Country } }> => {
    return apiClient.put('/trails/locations/countries', country);
  },

  deleteCountry: async (request: DeleteLocationRequest): Promise<{ data: { message: string } }> => {
    return apiClient.delete('/trails/locations/countries', { config: { data: request } });
  },

  // City operations
  getCities: async (countryId?: number): Promise<{ data: { cities: CityWithCountry[]; total: number } }> => {
    const url = countryId ? `/trails/locations/cities?countryId=${countryId}` : '/trails/locations/cities';
    return apiClient.get(url);
  },

  createCity: async (city: CreateCityRequest): Promise<{ data: { city: CityWithCountry } }> => {
    return apiClient.post('/trails/locations/cities', city);
  },

  updateCity: async (city: UpdateCityRequest): Promise<{ data: { city: CityWithCountry } }> => {
    return apiClient.put('/trails/locations/cities', city);
  },

  deleteCity: async (request: DeleteLocationRequest): Promise<{ data: { message: string } }> => {
    return apiClient.delete('/trails/locations/cities', { config: { data: request } });
  },

  // Helper operations
  checkDeletionPermission: async (type: 'country' | 'city', id: number): Promise<{ data: DeletionPermissionCheck }> => {
    return apiClient.get(`/trails/locations/check-deletion?type=${type}&id=${id}`);
  },
};

export default locationService;